import React, { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Settings,
  ArrowRight,
  ArrowLeft,
  Loader2,
  X,
  Phone,
  Mail,
  MessageCircle,
  Calendar,
  Clock,
  ChevronDown,
  Check,
  Eye,
  EyeOff,
  ExternalLink,
  Send,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { useAgent } from "../../context/AgentContext";
import Button from "../../components/Button";
import Toggle from "../../components/Toggle";
import { Input, Textarea } from "../../components/Input";

// Features list - autoReply is always enabled by default (not selectable)
const featuresList = [
  {
    id: "leadCapture",
    name: "Lead Capture",
    description: "Collect contact information from interested customers",
  },
  {
    id: "appointmentBooking",
    name: "Appointment Booking",
    description: "Let customers schedule appointments via chat",
  },
  {
    id: "gmailIntegration",
    name: "Gmail Integration",
    description: "Send emails to customers via your Gmail account",
  },
  {
    id: "humanHandoff",
    name: "Human Handoff",
    description: "Transfer complex queries to human agents",
  },
];

export default function StepFeatures() {
  const { goNext, goPrev } = useOutletContext();
  const { currentAgent, updateAgentFeatures, isLoading, error, clearError } =
    useAgent();

  // Initialize features state - will be updated by useEffect when currentAgent loads
  const [features, setFeatures] = useState(() => {
    const initial = {};
    featuresList.forEach((f) => {
      initial[f.id] = false;
    });
    return initial;
  });

  // Handoff config state
  const [handoffConfig, setHandoffConfig] = useState({
    phone: "",
    email: "",
    message: "",
  });
  const [showHandoffModal, setShowHandoffModal] = useState(false);
  const [handoffConfigTemp, setHandoffConfigTemp] = useState({
    phone: "",
    email: "",
    message: "",
  });
  const [handoffError, setHandoffError] = useState(null);

  // Booking config state
  const defaultBookingConfig = {
    timezone: "Africa/Nairobi",
    slot_duration_minutes: 30,
    buffer_minutes: 15,
    min_advance_hours: 1,
    max_advance_days: 30,
    working_hours: {
      "0": null, // Sunday
      "1": { start: "09:00", end: "17:00" },
      "2": { start: "09:00", end: "17:00" },
      "3": { start: "09:00", end: "17:00" },
      "4": { start: "09:00", end: "17:00" },
      "5": { start: "09:00", end: "17:00" },
      "6": null, // Saturday
    },
    breaks: [],
    confirmation_message: "",
  };
  const [bookingConfig, setBookingConfig] = useState(defaultBookingConfig);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingConfigTemp, setBookingConfigTemp] = useState(defaultBookingConfig);
  const [bookingError, setBookingError] = useState(null);

  // Gmail config state
  const defaultEmailConfig = {
    gmail_address: "",
    app_password: "",
    sender_name: "",
    signature: "",
  };
  const [emailConfig, setEmailConfig] = useState(defaultEmailConfig);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailConfigTemp, setEmailConfigTemp] = useState(defaultEmailConfig);
  const [emailError, setEmailError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Dropdown open states for booking config modal
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Dropdown options
  const timezoneOptions = [
    { value: "Africa/Nairobi", label: "East Africa (Nairobi)" },
    { value: "Africa/Lagos", label: "West Africa (Lagos)" },
    { value: "Africa/Johannesburg", label: "South Africa (Johannesburg)" },
    { value: "Africa/Cairo", label: "Egypt (Cairo)" },
    { value: "Europe/London", label: "UK (London)" },
    { value: "Europe/Paris", label: "Central Europe (Paris)" },
    { value: "America/New_York", label: "US Eastern (New York)" },
    { value: "America/Los_Angeles", label: "US Pacific (Los Angeles)" },
    { value: "Asia/Dubai", label: "UAE (Dubai)" },
    { value: "Asia/Singapore", label: "Singapore" },
  ];

  const slotDurationOptions = [
    { value: 15, label: "15 minutes" },
    { value: 30, label: "30 minutes" },
    { value: 45, label: "45 minutes" },
    { value: 60, label: "1 hour" },
    { value: 90, label: "1.5 hours" },
    { value: 120, label: "2 hours" },
  ];

  const minAdvanceOptions = [
    { value: 1, label: "1 hour" },
    { value: 2, label: "2 hours" },
    { value: 4, label: "4 hours" },
    { value: 12, label: "12 hours" },
    { value: 24, label: "24 hours" },
    { value: 48, label: "48 hours" },
  ];

  const maxAdvanceOptions = [
    { value: 7, label: "1 week" },
    { value: 14, label: "2 weeks" },
    { value: 30, label: "1 month" },
    { value: 60, label: "2 months" },
    { value: 90, label: "3 months" },
  ];

  // Time options for working hours (30-min intervals)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const value = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
      timeOptions.push({ value, label });
    }
  }

  const formatTimeLabel = (time24) => {
    if (!time24) return '';
    const [hour, min] = time24.split(':').map(Number);
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.booking-dropdown')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Update features when currentAgent loads or changes (for resume flow)
  useEffect(() => {
    if (currentAgent?.features) {
      const newFeatures = {};
      featuresList.forEach((f) => {
        // Use saved value if exists, otherwise false
        newFeatures[f.id] = currentAgent.features[f.id] === true;
      });
      setFeatures(newFeatures);
    }
    // Also load handoff config if it exists
    if (currentAgent?.handoff_config) {
      setHandoffConfig({
        phone: currentAgent.handoff_config.phone || "",
        email: currentAgent.handoff_config.email || "",
        message: currentAgent.handoff_config.message || "",
      });
    }
    // Load booking config if it exists
    if (currentAgent?.booking_config) {
      setBookingConfig({
        ...defaultBookingConfig,
        ...currentAgent.booking_config,
      });
    }
    // Load email config if it exists
    if (currentAgent?.email_config) {
      setEmailConfig({
        ...defaultEmailConfig,
        ...currentAgent.email_config,
      });
    }
  }, [currentAgent]);

  const toggleFeature = (featureId) => {
    // If enabling humanHandoff, show the config modal instead of toggling directly
    if (featureId === "humanHandoff" && !features.humanHandoff) {
      setHandoffConfigTemp({
        phone: handoffConfig.phone || "",
        email: handoffConfig.email || "",
        message: handoffConfig.message || "",
      });
      setHandoffError(null);
      setShowHandoffModal(true);
      return;
    }

    // If enabling appointmentBooking, show the booking config modal
    if (featureId === "appointmentBooking" && !features.appointmentBooking) {
      setBookingConfigTemp({ ...bookingConfig });
      setBookingError(null);
      setShowBookingModal(true);
      return;
    }

    // If enabling gmailIntegration, show the email config modal
    if (featureId === "gmailIntegration" && !features.gmailIntegration) {
      setEmailConfigTemp({ ...emailConfig });
      setEmailError(null);
      setTestEmailResult(null);
      setShowEmailModal(true);
      return;
    }

    setFeatures((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
    if (error) clearError();
  };

  const handleSaveHandoffConfig = () => {
    // Validate at least one contact method
    if (!handoffConfigTemp.phone.trim() && !handoffConfigTemp.email.trim()) {
      setHandoffError(
        "Please provide at least one contact method (phone or email)",
      );
      return;
    }

    // Save config and enable the feature
    setHandoffConfig({
      phone: handoffConfigTemp.phone.trim(),
      email: handoffConfigTemp.email.trim(),
      message: handoffConfigTemp.message.trim(),
    });
    setFeatures((prev) => ({
      ...prev,
      humanHandoff: true,
    }));
    setShowHandoffModal(false);
  };

  const handleCloseHandoffModal = () => {
    setShowHandoffModal(false);
    setHandoffError(null);
  };

  // Booking modal handlers
  const handleSaveBookingConfig = () => {
    // Validate at least one working day
    const hasWorkingDay = Object.values(bookingConfigTemp.working_hours).some(
      (hours) => hours !== null
    );
    if (!hasWorkingDay) {
      setBookingError("Please enable at least one working day");
      return;
    }

    // Save config and enable the feature
    setBookingConfig({ ...bookingConfigTemp });
    setFeatures((prev) => ({
      ...prev,
      appointmentBooking: true,
    }));
    setShowBookingModal(false);
  };

  const handleCloseBookingModal = () => {
    setShowBookingModal(false);
    setBookingError(null);
  };

  // Email modal handlers
  const handleSaveEmailConfig = () => {
    // Validate required fields
    if (!emailConfigTemp.gmail_address.trim()) {
      setEmailError("Please enter your Gmail address");
      return;
    }
    if (!emailConfigTemp.app_password.trim()) {
      setEmailError("Please enter your App Password");
      return;
    }
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailConfigTemp.gmail_address.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Save config and enable the feature
    setEmailConfig({
      gmail_address: emailConfigTemp.gmail_address.trim(),
      app_password: emailConfigTemp.app_password.trim(),
      sender_name: emailConfigTemp.sender_name.trim(),
      signature: emailConfigTemp.signature.trim(),
    });
    setFeatures((prev) => ({
      ...prev,
      gmailIntegration: true,
    }));
    setShowEmailModal(false);
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailError(null);
    setTestEmailResult(null);
    setShowPassword(false);
  };

  const toggleWorkingDay = (dayNum) => {
    setBookingConfigTemp((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayNum]: prev.working_hours[dayNum]
          ? null
          : { start: "09:00", end: "17:00" },
      },
    }));
  };

  const updateWorkingHours = (dayNum, field, value) => {
    setBookingConfigTemp((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayNum]: {
          ...prev.working_hours[dayNum],
          [field]: value,
        },
      },
    }));
  };

  const handleContinue = async () => {
    try {
      // Include handoff_config if humanHandoff is enabled
      const updateData = { features };
      if (
        features.humanHandoff &&
        (handoffConfig.phone || handoffConfig.email)
      ) {
        updateData.handoff_config = handoffConfig;
      }
      // Include booking_config if appointmentBooking is enabled
      if (features.appointmentBooking) {
        updateData.booking_config = bookingConfig;
      }
      // Include email_config if gmailIntegration is enabled
      if (features.gmailIntegration) {
        updateData.email_config = emailConfig;
      }
      await updateAgentFeatures(updateData);
      goNext();
    } catch (err) {
      console.error("Failed to save features:", err);
    }
  };

  const enabledCount = Object.values(features).filter(Boolean).length;

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Features</h1>
            <p className="text-text-secondary">Choose what your AI can do</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
          <button onClick={clearError} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Features List */}
      <div className="card divide-y divide-border">
        {featuresList.map((feature, index) => (
          <div
            key={feature.id}
            className="p-5 animate-slide-up"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <Toggle
              checked={features[feature.id] || false}
              onChange={() => toggleFeature(feature.id)}
              label={feature.name}
              description={feature.description}
              disabled={isLoading}
            />
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-secondary">Features enabled</span>
          <span className="font-semibold text-primary">
            {enabledCount} / {featuresList.length}
          </span>
        </div>
        <div className="mt-2 h-2 bg-white rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
            style={{ width: `${(enabledCount / featuresList.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          icon={isLoading ? Loader2 : ArrowRight}
          iconPosition="right"
          onClick={handleContinue}
          disabled={isLoading}
          className={isLoading ? "[&_svg]:animate-spin" : ""}
        >
          {isLoading ? "Saving..." : "Continue"}
        </Button>
      </div>

      {/* Human Handoff Config Modal */}
      {showHandoffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-scale-in">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    Human Handoff Setup
                  </h2>
                  <p className="text-sm text-text-secondary">
                    How can customers reach a human?
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseHandoffModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1 scrollbar-thin">
              <p className="text-sm text-text-secondary">
                When a customer requests to speak with a human or when the AI is
                unable to respond, your AI will provide this contact
                information. You need at least one contact method.
              </p>

              {handoffError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {handoffError}
                </div>
              )}

              <Input
                label="Phone Number"
                icon={Phone}
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={handoffConfigTemp.phone}
                onChange={(e) =>
                  setHandoffConfigTemp((prev) => ({
                    ...prev,
                    phone: e.target.value,
                  }))
                }
                helper="Customers can call or text this number"
              />

              <Input
                label="Email Address"
                icon={Mail}
                type="email"
                placeholder="support@yourcompany.com"
                value={handoffConfigTemp.email}
                onChange={(e) =>
                  setHandoffConfigTemp((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                helper="Customers can email for human support"
              />

              <Textarea
                label="Custom Message (Optional)"
                placeholder="Our team is available Monday-Friday, 9am-5pm. We'll get back to you within 24 hours."
                value={handoffConfigTemp.message}
                onChange={(e) =>
                  setHandoffConfigTemp((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }))
                }
                rows={3}
                helper="Additional info shown when customer requests human support"
              />

              {/* Preview */}
              {(handoffConfigTemp.phone || handoffConfigTemp.email) && (
                <div className="p-4 bg-gray-50 rounded-xl border border-border">
                  <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                    Preview
                  </p>
                  <div className="bg-white rounded-lg p-3 border border-border">
                    <p className="text-sm text-text-primary">
                      I'd be happy to connect you with our team! You can reach
                      us at:
                    </p>
                    {handoffConfigTemp.phone && (
                      <p className="text-sm text-primary mt-1">
                        Phone: {handoffConfigTemp.phone}
                      </p>
                    )}
                    {handoffConfigTemp.email && (
                      <p className="text-sm text-primary mt-1">
                        Email: {handoffConfigTemp.email}
                      </p>
                    )}
                    {handoffConfigTemp.message && (
                      <p className="text-sm text-text-secondary mt-2 italic">
                        {handoffConfigTemp.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-gray-50 rounded-b-2xl flex-shrink-0">
              <Button variant="ghost" onClick={handleCloseHandoffModal}>
                Cancel
              </Button>
              <Button onClick={handleSaveHandoffConfig}>
                Enable Human Handoff
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Booking Config Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-scale-in">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    Appointment Booking Setup
                  </h2>
                  <p className="text-sm text-text-secondary">
                    Configure your availability
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseBookingModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              {bookingError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {bookingError}
                </div>
              )}

              {/* Model Recommendation Notice */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Recommended: Use GPT Models</p>
                  <p className="text-sm text-blue-700 mt-1">
                    For appointment booking to work properly, we recommend using GPT models such as <strong>GPT-4o</strong> or <strong>GPT-4o-mini</strong>. You can configure this in the Model step.
                  </p>
                </div>
              </div>

              {/* Basic Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-text-primary">Basic Settings</h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Timezone Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Timezone
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() => setOpenDropdown(openDropdown === 'timezone' ? null : 'timezone')}
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary truncate">
                          {timezoneOptions.find(o => o.value === bookingConfigTemp.timezone)?.label || 'Select timezone'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${openDropdown === 'timezone' ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === 'timezone' && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {timezoneOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({ ...prev, timezone: option.value }));
                                setOpenDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span className={bookingConfigTemp.timezone === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                                {option.label}
                              </span>
                              {bookingConfigTemp.timezone === option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Slot Duration Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Slot Duration
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() => setOpenDropdown(openDropdown === 'duration' ? null : 'duration')}
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary">
                          {slotDurationOptions.find(o => o.value === bookingConfigTemp.slot_duration_minutes)?.label || 'Select duration'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${openDropdown === 'duration' ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === 'duration' && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {slotDurationOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({ ...prev, slot_duration_minutes: option.value }));
                                setOpenDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span className={bookingConfigTemp.slot_duration_minutes === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                                {option.label}
                              </span>
                              {bookingConfigTemp.slot_duration_minutes === option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Minimum Advance Booking Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Minimum Advance Booking
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() => setOpenDropdown(openDropdown === 'minAdvance' ? null : 'minAdvance')}
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary">
                          {minAdvanceOptions.find(o => o.value === bookingConfigTemp.min_advance_hours)?.label || 'Select minimum'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${openDropdown === 'minAdvance' ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === 'minAdvance' && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {minAdvanceOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({ ...prev, min_advance_hours: option.value }));
                                setOpenDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span className={bookingConfigTemp.min_advance_hours === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                                {option.label}
                              </span>
                              {bookingConfigTemp.min_advance_hours === option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Max Advance Days Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Max Advance Days
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() => setOpenDropdown(openDropdown === 'maxAdvance' ? null : 'maxAdvance')}
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary">
                          {maxAdvanceOptions.find(o => o.value === bookingConfigTemp.max_advance_days)?.label || 'Select maximum'}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${openDropdown === 'maxAdvance' ? 'rotate-180' : ''}`} />
                      </button>
                      {openDropdown === 'maxAdvance' && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {maxAdvanceOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({ ...prev, max_advance_days: option.value }));
                                setOpenDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span className={bookingConfigTemp.max_advance_days === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                                {option.label}
                              </span>
                              {bookingConfigTemp.max_advance_days === option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-text-primary">Working Hours</h3>
                    <p className="text-sm text-text-secondary">
                      Set your available days and hours for appointments
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {[
                    { num: "1", name: "Monday", short: "Mon" },
                    { num: "2", name: "Tuesday", short: "Tue" },
                    { num: "3", name: "Wednesday", short: "Wed" },
                    { num: "4", name: "Thursday", short: "Thu" },
                    { num: "5", name: "Friday", short: "Fri" },
                    { num: "6", name: "Saturday", short: "Sat" },
                    { num: "0", name: "Sunday", short: "Sun" },
                  ].map((day) => {
                    const isEnabled = bookingConfigTemp.working_hours[day.num] !== null;
                    return (
                      <div
                        key={day.num}
                        className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                          isEnabled
                            ? 'border-primary/20 bg-primary/5'
                            : 'border-border bg-gray-50'
                        }`}
                      >
                        <label className="flex items-center gap-3 min-w-[120px] cursor-pointer">
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isEnabled
                              ? 'bg-primary border-primary'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {isEnabled && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => toggleWorkingDay(day.num)}
                            className="sr-only"
                          />
                          <span className={`text-sm font-medium ${isEnabled ? 'text-text-primary' : 'text-text-muted'}`}>
                            {day.name}
                          </span>
                        </label>

                        {isEnabled ? (
                          <div className="flex items-center gap-2 flex-1">
                            {/* Start Time Dropdown */}
                            <div className="relative booking-dropdown flex-1">
                              <button
                                type="button"
                                onClick={() => setOpenDropdown(openDropdown === `start-${day.num}` ? null : `start-${day.num}`)}
                                className="w-full px-3 py-2 bg-white border border-border rounded-lg text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                              >
                                <span className="text-sm text-text-primary">
                                  {formatTimeLabel(bookingConfigTemp.working_hours[day.num]?.start)}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${openDropdown === `start-${day.num}` ? 'rotate-180' : ''}`} />
                              </button>
                              {openDropdown === `start-${day.num}` && (
                                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {timeOptions.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => {
                                        updateWorkingHours(day.num, "start", option.value);
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                                    >
                                      <span className={bookingConfigTemp.working_hours[day.num]?.start === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                                        {option.label}
                                      </span>
                                      {bookingConfigTemp.working_hours[day.num]?.start === option.value && (
                                        <Check className="w-4 h-4 text-primary" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            <span className="text-text-muted text-sm">to</span>

                            {/* End Time Dropdown */}
                            <div className="relative booking-dropdown flex-1">
                              <button
                                type="button"
                                onClick={() => setOpenDropdown(openDropdown === `end-${day.num}` ? null : `end-${day.num}`)}
                                className="w-full px-3 py-2 bg-white border border-border rounded-lg text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                              >
                                <span className="text-sm text-text-primary">
                                  {formatTimeLabel(bookingConfigTemp.working_hours[day.num]?.end)}
                                </span>
                                <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${openDropdown === `end-${day.num}` ? 'rotate-180' : ''}`} />
                              </button>
                              {openDropdown === `end-${day.num}` && (
                                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {timeOptions.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => {
                                        updateWorkingHours(day.num, "end", option.value);
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                                    >
                                      <span className={bookingConfigTemp.working_hours[day.num]?.end === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                                        {option.label}
                                      </span>
                                      {bookingConfigTemp.working_hours[day.num]?.end === option.value && (
                                        <Check className="w-4 h-4 text-primary" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-text-muted italic">Closed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Google Calendar Integration */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zm-9 15h-3v-3h3v3zm0-4.5h-3v-3h3v3zm0-4.5h-3V6h3v3zm4.5 9h-3v-3h3v3zm0-4.5h-3v-3h3v3zm0-4.5h-3V6h3v3zm4.5 9h-3v-3h3v3zm0-4.5h-3v-3h3v3zm0-4.5h-3V6h3v3z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-text-primary">Google Calendar</h3>
                    <p className="text-sm text-text-secondary">
                      Sync appointments with your calendar
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white border border-border flex items-center justify-center shadow-sm">
                        <svg className="w-6 h-6" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">Connect Google Calendar</p>
                        <p className="text-xs text-text-muted">Auto-sync bookings to your calendar</p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                      Coming Soon
                    </span>
                  </div>
                </div>
              </div>

              {/* Confirmation Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Confirmation Message (Optional)
                </label>
                <Textarea
                  placeholder="Your appointment is confirmed for {date} at {time}. We look forward to seeing you!"
                  value={bookingConfigTemp.confirmation_message}
                  onChange={(e) =>
                    setBookingConfigTemp((prev) => ({
                      ...prev,
                      confirmation_message: e.target.value,
                    }))
                  }
                  rows={2}
                  helper="Use {date}, {time}, {customer_name} as placeholders"
                />
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-gray-50 rounded-b-2xl flex-shrink-0">
              <Button variant="ghost" onClick={handleCloseBookingModal}>
                Cancel
              </Button>
              <Button onClick={handleSaveBookingConfig}>
                Enable Appointment Booking
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Gmail Integration Config Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-scale-in">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    Gmail Integration Setup
                  </h2>
                  <p className="text-sm text-text-secondary">
                    Connect your Gmail to send emails
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseEmailModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
              {emailError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {emailError}
                </div>
              )}

              {testEmailResult && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                  testEmailResult.success
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {testEmailResult.success ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  {testEmailResult.message}
                </div>
              )}

              {/* Instructions Section */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 mb-2">How to get your App Password</h3>
                    <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                      <li>Go to your Google Account settings at <a href="https://myaccount.google.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">myaccount.google.com</a></li>
                      <li>Navigate to <strong>Security</strong> → <strong>2-Step Verification</strong> (must be enabled)</li>
                      <li>Scroll down and click on <strong>App passwords</strong></li>
                      <li>Select "Mail" and "Other (Custom name)" → Enter "Botibonga"</li>
                      <li>Click <strong>Generate</strong> and copy the 16-character password</li>
                    </ol>
                    <p className="text-xs text-blue-600 mt-3">
                      Note: App passwords only work if 2-Step Verification is enabled on your Google account.
                    </p>
                  </div>
                </div>
              </div>

              {/* Gmail Address */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Gmail Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="email"
                    placeholder="yourbusiness@gmail.com"
                    value={emailConfigTemp.gmail_address}
                    onChange={(e) =>
                      setEmailConfigTemp((prev) => ({
                        ...prev,
                        gmail_address: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <p className="text-xs text-text-muted">
                  The Gmail address you want to send emails from. This must be a Gmail account.
                </p>
              </div>

              {/* App Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  App Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="xxxx xxxx xxxx xxxx"
                    value={emailConfigTemp.app_password}
                    onChange={(e) =>
                      setEmailConfigTemp((prev) => ({
                        ...prev,
                        app_password: e.target.value.replace(/\s/g, ''),
                      }))
                    }
                    className="w-full px-4 py-3 pr-12 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-text-muted" />
                    ) : (
                      <Eye className="w-5 h-5 text-text-muted" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-text-muted">
                  The 16-character App Password generated from your Google Account. This is NOT your regular Gmail password.
                </p>
              </div>

              {/* Sender Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Sender Display Name <span className="text-text-muted font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Your Business Name"
                  value={emailConfigTemp.sender_name}
                  onChange={(e) =>
                    setEmailConfigTemp((prev) => ({
                      ...prev,
                      sender_name: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                <p className="text-xs text-text-muted">
                  The name that appears in the "From" field. If left empty, your Gmail address will be shown.
                </p>
              </div>

              {/* Signature */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Email Signature <span className="text-text-muted font-normal">(Optional)</span>
                </label>
                <Textarea
                  placeholder="Best regards,&#10;The [Your Business] Team&#10;&#10;Phone: +1 234 567 890&#10;Website: www.yourbusiness.com"
                  value={emailConfigTemp.signature}
                  onChange={(e) =>
                    setEmailConfigTemp((prev) => ({
                      ...prev,
                      signature: e.target.value,
                    }))
                  }
                  rows={4}
                />
                <p className="text-xs text-text-muted">
                  This signature will be automatically added to the end of all emails sent by your AI assistant.
                </p>
              </div>

              {/* Connect OAuth Coming Soon */}
              <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white border border-border flex items-center justify-center shadow-sm">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">Connect with Google OAuth</p>
                      <p className="text-xs text-text-muted">One-click sign in without App Password</p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-gray-50 rounded-b-2xl flex-shrink-0">
              <Button variant="ghost" onClick={handleCloseEmailModal}>
                Cancel
              </Button>
              <Button onClick={handleSaveEmailConfig} icon={Send}>
                Enable Gmail Integration
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
